import React, { useState } from 'react';
import axios from "axios"; // <-- Added
import { Mail, Phone, MapPin, Send, Github, Linkedin } from 'lucide-react';
import { personalInfo } from '../data/mock';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/send-mail`, {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      });

      toast({
        title: "Message Sent!",
        description: "Thank you for reaching out. I'll get back to you soon 📩",
      });

      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Try again later ❌",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: personalInfo.email,
      href: `mailto:${personalInfo.email}`,
      color: '#10b981'
    },
    {
      icon: Phone,
      label: 'Phone',
      value: personalInfo.phone,
      href: `tel:${personalInfo.phone}`,
      color: '#06b6d4'
    },
    {
      icon: MapPin,
      label: 'Location',
      value: personalInfo.location,
      href: null,
      color: '#f97316'
    }
  ];

  return (
    <section id="contact" className="py-20 bg-[#121214]">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">

          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Get In <span className="text-[#10b981]">Touch</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#10b981] to-[#06b6d4] mx-auto mb-4"></div>
            <p className="text-[#a3a3a3] max-w-2xl mx-auto">
              Have a question or want to work together? Feel free to reach out!
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-white mb-6">Let's Connect</h3>
                <p className="text-[#a3a3a3] mb-8">
                  I'm always open to discussing new projects, creative ideas, or opportunities to be part of your vision.
                </p>
              </div>

              <div className="space-y-4">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  const content = (
                    <Card
                      key={index}
                      className="bg-[#0a0a0b] border-[#1f1f23] hover:border-[#10b981] transition-all duration-300 group cursor-pointer"
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div
                          className="w-12 h-12 rounded-lg bg-[#121214] flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                          style={{ color: info.color }}
                        >
                          <Icon size={20} />
                        </div>
                        <div>
                          <p className="text-[#a3a3a3] text-sm">{info.label}</p>
                          <p className="text-white font-medium">{info.value}</p>
                        </div>
                      </CardContent>
                    </Card>
                  );

                  return info.href ? (
                    <a key={index} href={info.href} target="_blank" rel="noopener noreferrer">
                      {content}
                    </a>
                  ) : (
                    content
                  );
                })}
              </div>

              <div>
                <p className="text-[#a3a3a3] mb-4">Connect with me on</p>
                <div className="flex gap-4">
                  <a
                    href={personalInfo.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-lg bg-[#0a0a0b] border border-[#1f1f23] flex items-center justify-center text-[#a3a3a3] hover:text-[#10b981] hover:border-[#10b981] transition-all duration-300 hover:scale-110"
                  >
                    <Github size={20} />
                  </a>
                  <a
                    href={personalInfo.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-lg bg-[#0a0a0b] border border-[#1f1f23] flex items-center justify-center text-[#a3a3a3] hover:text-[#06b6d4] hover:border-[#06b6d4] transition-all duration-300 hover:scale-110"
                  >
                    <Linkedin size={20} />
                  </a>
                </div>
              </div>
            </div>

            <Card className="bg-[#0a0a0b] border-[#1f1f23]">
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-white mb-2 font-medium">Name</label>
                    <Input
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="bg-[#121214] border-[#1f1f23] text-white placeholder:text-[#a3a3a3] focus:border-[#10b981]"
                    />
                  </div>

                  <div>
                    <label className="block text-white mb-2 font-medium">Email</label>
                    <Input
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      className="bg-[#121214] border-[#1f1f23] text-white placeholder:text-[#a3a3a3] focus:border-[#10b981]"
                    />
                  </div>

                  <div>
                    <label className="block text-white mb-2 font-medium">Subject</label>
                    <Input
                      name="subject"
                      type="text"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="What's this about?"
                      className="bg-[#121214] border-[#1f1f23] text-white placeholder:text-[#a3a3a3] focus:border-[#10b981]"
                    />
                  </div>

                  <div>
                    <label className="block text-white mb-2 font-medium">Message</label>
                    <Textarea
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Your message..."
                      rows={5}
                      className="bg-[#121214] border-[#1f1f23] text-white placeholder:text-[#a3a3a3] focus:border-[#10b981] resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#10b981] hover:bg-[#059669] text-white"
                  >
                    {isSubmitting ? "Sending..." : (
                      <>
                        <Send size={18} className="mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
